/***********************************************************
  MAIN.JS
************************************************************/

import { ConfigurationError, DataLoadFailure, FetchRejected } from './errors';

import * as D from './data-utils';
// import R from 'ramda';

import $ from 'jquery';
import browser from 'detect-browser';

import Events from './events';
import { init as _init } from './analytics';

import { getFeeds } from './feeds';
import { filterAPIEndpoints, prioritiseFeeds } from '../conf/api-endpoints';
import Resources from './resources';
import Router from './router';
let router;
import App from './app';

import './partials-compiled';
import './templates-compiled';
import { initHelpers } from './helpers/handlebarsHelpers';
import { initButtons } from './component/ui-transform-button';
import { getImdSettings } from './conf';
import { parseJSON } from './util';
import { startAnalytics } from './analytics/ga4-tracker';
import { checkQueryParam } from './url-switches';
import { createFeedLoader } from './feed-loader';

// checkSwitch :: (String, Bool) -> Bool
const checkSwitch = (name, fallback) => checkQueryParam(location.search, name, fallback);

const devMode = checkSwitch('devmode');
const devSwithes = {
  forceScreenSaverOff: checkSwitch('screensaver-off', devMode),
  scaleToScreen: checkSwitch('scale-to-screen', devMode),
  disableServiceworker: checkSwitch('disable-sw', devMode),
  disableSessionReset: checkSwitch('disable-reset', devMode)
};


const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * 60 * SECOND;
const TEN_MINUTES = 10 * 60 * SECOND;
const refreshPeriod = HOUR;

const nextRefresh = () => refreshPeriod + Math.round((Math.random() * 5 - 2.5) * MINUTE);

let resetToStartTimeout = 30;

const feedLoader = createFeedLoader(
  feed => getData(feed),
  ({ name, lang }, data) => App.saveLocally(data, name, lang ? lang : 'all')
);

// languageFeeds :: (Lang, Context) -> [Feed]
//    Every feed the views read in a language: its localized feeds plus the
//    ones shared by all languages (lunch, opening hours)
const languageFeeds = (lang, context) => {
  const { priority, secondary } = prioritiseFeeds(
    lang,
    context.langs,
    context.imdSettings,
    { api_methods: 'scimddata', include: false }
  );
  return priority.concat(secondary.filter(feed => !feed.lang || feed.lang == lang));
};

// ensureLanguageData :: Context -> Lang -> Promise
//    Loads the feeds of a language unless they are already stored. At
//    start-up only the priority feeds of the current language load right
//    away, the rest 5 s later, so opening a view or switching language before
//    that would leave the view without data.
const ensureLanguageData = context => (lang = D.lang()) => {
  if (!context.langs) return Promise.resolve();
  return feedLoader.ensure(languageFeeds(lang, context))
    .then(loadedNow => {
      if (loadedNow) Events.dispatchEvent('data:updated');
    });
};

let isLandscape = false;
let hasSession = false;

// const babelHack = IMDApp.__esModule && IMDApp.default;
// let imdApp;

// Load configurations and start the IMD
export async function init (imdApp, context) {

  startAnalytics({ imdHash: imdApp.hash });
  const imdSettings = await getImdSettings(imdApp.hash);

  viewport(imdSettings);

  Object.assign(context, {
    imdId: imdSettings.imdId,
    imdHash: imdApp.hash,
    imdSettings,
    scName: imdSettings.scName,
    Feeds: getFeeds(imdApp.hash),
    devMode,
    disableServiceworker: !!imdSettings.disableServiceworker || devSwithes.disableServiceworker,
    screensaverOn: devSwithes.forceScreenSaverOff ? false : imdSettings.screensaverOn,
    disabledLanguages: imdSettings.disabledLanguages,
    thirdPartyLinks: imdSettings.thirdPartyLinks,
    isLandscape: imdSettings.isLandscape,
    resetToStartTimeout: Number.isInteger(imdSettings.resetToStartTimeout) ? imdSettings.resetToStartTimeout : 30,
    scaleToScreen: imdSettings.scaleToScreen,
    statusReportingEndPoint: imdSettings.statusReportingEndPoint,
    maxFeatureLifts: imdSettings.maxFeatureLifts,
    disablePinchingZoom: imdSettings.disablePinchingZoom,
    debugPan: imdSettings.debugPan
  });

  context.ensureLanguageData = ensureLanguageData(context);
  context.isLanguageDataLoaded = (lang = D.lang()) =>
    !context.langs || feedLoader.isLoaded(languageFeeds(lang, context));
  // Start loading as soon as the language is picked, views wait for it in the router
  Events.addEventListener('language:change', ({ detail: lang }) => {
    context.ensureLanguageData(lang).catch(console.error);
  });


  router = Router(context);

  imdApp.setContext(context);
  _init(context);
  setOrienation(context.isLandscape);
  addListeners(context);

  if (devMode ? devSwithes.scaleToScreen : context.scaleToScreen) {
    scaleToScreen();
  }

  App.setTimerFunctions(launchScreensaver(context), resetToStartView);

  const [resources] = await Promise.all([
    Resources.load(context, document.body),
    loadApp(context)
  ]);

  context.resources = resources;
  imdApp.setResources(resources);

  initHelpers(context);
  initButtons(context);
  bindUITransformButtons(context);
  await startApp(context);

  refreshDataPeriodically(nextRefresh(), context);
}


/**
 * @param {number} timeInMills
 * @param {any} context
 */
function refreshDataPeriodically(timeInMills, context) {
  try {
    const [clock] = new Date(Date.now() + timeInMills).toTimeString().split(' ');
    const time = timeInMills / 6e4;
    console.log(`Next refresh in ${time.toFixed(1)} mins at ${clock}`);
  } catch (error) { /* Ignore */ }
  
  setTimeout(async () => {
    console.log('Refreshing data');
    try {
      await loadApp(context);
      refreshDataPeriodically(nextRefresh(), context);
    } catch (e) {
      // Try again after shorter period
      refreshDataPeriodically(TEN_MINUTES, context);
      console.error(e);
    }
  }, timeInMills);
}

async function loadApp(context) {

  const checkImd = ({imds = []}) => {
    const notFound = !imds.some(({id}) => id == context.imdId);
    if (notFound) {
      throw new ConfigurationError(
        `IMD id '${context.imdId}' not available in scimddata`
      );
    }
  };

  console.log('Loading feeds ...');
  const [api] = filterAPIEndpoints('scimddata');

  // First fetch scimmddata to get languages, then fetch feeds for every language
  const [feed] = api.asFeed(context.imdSettings);
  const scimddata = await getData(feed);
  checkImd(scimddata.shoppingCenterImdData);
  App.saveLocally(scimddata, 'scimddata', 'all');

  context.langs = getLanguages(scimddata);
  return loadFeeds(context);
}

//get languages used in this shopping center
function getLanguages({ shoppingCenterImdData }) {

  const { languages } = shoppingCenterImdData;

  if (!languages) {
    console.error('Missing IMD language settings in portal');
    return [];
  }

  getData({
    name: 'l10n',
    url_client: '/l10n',
  })
  .then(l10n => {
    Object.entries(l10n).forEach(([lang, translations]) => {
      App.saveLocally(translations, 'translations', lang);
    })
  })

  return languages;
}

//load data in all languages
async function loadFeeds(context) {

  const getAndSave = feed => feedLoader.load(feed);
  const scheduledAt = Date.now();

  const prioritised_feeds = prioritiseFeeds(
    D.lang(), 
    context.langs, 
    context.imdSettings, 
    { api_methods: 'scimddata', include: false }
  );

  await Promise.all(
    prioritised_feeds.priority.map(getAndSave)
  )
  Events.dispatchEvent('data:updated');

  // No need to wait these updates to land
  setTimeout(() => {
    Promise.all(
      prioritised_feeds.secondary.map(feed => feedLoader.loadUnlessLoadedSince(feed, scheduledAt))
    )
    .then(() => Events.dispatchEvent('data:updated'))
    .catch(error => {
      console.error(error);
    })
  }, 5 * SECOND);
}


function getData(feed) {
  
  return fetch(feed.url_client)
    .then(async (res) => {
      const json = await parseJSON(res);
      if (res.ok) return json;
      throw new DataLoadFailure(res, json);
    })
    .catch(e => {
      if (e instanceof TypeError) throw new FetchRejected(e);
      else throw e; // fall through
    });
}

async function startApp(context) {
  console.log('Starting IMD ...');
  await router.startRouting();
  Events.dispatchEvent('app:start');
}

function setOrienation(isLandscape) {
  isLandscape ? $('body').addClass('landscape') : $('body').addClass('portrait');
}

//Screen is inactive - launch screen saver
function launchScreensaver(context) {
  const { screensaverOn } = context;
  return function startScreensaver () {
    clearTimeout(window.screensaverTimer);
    if (screensaverOn == 'media-playlist') {
      Events.dispatchEvent('screensaver:start');
    }
  }
}

function resetToStartView() {
  if (!devSwithes.disableSessionReset) {
    Events.dispatchEvent('session:end');
  
    hasSession = false;

    const defaultLang = D.defaultLang();
    if(defaultLang) {
      App.switchLanguage(defaultLang);
    }
    App.goTo('start');
    changeScreenModeTo(ScreenMode.CENTER);
    $('.more-popup').hide;
  }
}


//Screen is active
function resetActive() {
  if (!hasSession) {
    hasSession = true;
    Events.dispatchEvent('session:start');
  }

  App.resetTimers();
}

const ScreenMode = {
  CENTER: 'screen-mode-center',
  DOWN: 'screen-mode-down'
};

function bindUITransformButtons(context) {

  const handleEvent = (ifMode, toMode) => () => {
    const currentScreenMode = getScreenMode();
    if (currentScreenMode == ifMode) {
      changeScreenModeTo(toMode, context);
    }
  };

  const { CENTER, DOWN } = ScreenMode;
  $('#switch-mode-button-top button').on('click', handleEvent(DOWN, CENTER));
  $('#switch-mode-button-bottom button').on('click', handleEvent(CENTER, DOWN));
}

function getScreenMode() {
  const container = $('#page-container');
  if (container.hasClass('screen-mode-center')) {
    return ScreenMode.CENTER;
  } else if (container.hasClass('screen-mode-down')) {
    return ScreenMode.DOWN;
  } else {
    return ScreenMode.CENTER;
  }
}

function changeScreenModeTo(screenMode, context) {

  if(isLandscape) {
    return;
  }

  const container = $('#page-container');
  container.removeClass(ScreenMode.CENTER);
  container.removeClass(ScreenMode.DOWN);
  container.addClass(screenMode);
  if (context) {
    Events.dispatchEvent('ui:transform', screenMode.replace('screen-mode-', ''));
  }
}



function setOnline(isOnline) {
  App.setOnlineStatus(isOnline);
  setShowMoreMenu(isOnline);
}

function setShowMoreMenu(show) {
  show ? $('.main-minor-link--more').show() : $('.main-minor-link--more').hide();
}

function addListeners (context) {

  $(document).on('click', '#homebutton', function () {
    App.goTo('start');
  });

  //reset counter when the screen is touched
  Events.addEventListener('user:activity', _ => resetActive());

  Events.addEventListener('app:online', _ => setOnline(true));
  Events.addEventListener('app:offline', _ => setOnline(false));

  window.addEventListener('beforeunload', event => {
    Events.dispatchEvent('app:close');
  });
}

/**
 * @param {InstanceSettings} imdSettings
 */
function viewport({ viewport }) {
  if (viewport) {
    const { transform, transform_origin } = viewport;
    transform_origin && (document.body.style.transformOrigin = transform_origin);
    transform && (document.body.style.transform = transform);
    document.body.style.width = '1080px';
  }
}

function scaleToScreen() {

  const page_container = document.getElementById('page-container');

  function setScale(el, scale, widthDiff) {
    el.style.width = `calc(100% / ${scale})`; // Counter scaling to get width correct
    el.style.transform = `scale(${scale}, ${scale})`;
    el.style.transformOrigin = 'left top';
    App.saveLocally({scale}, 'screenScale', 'all');
  }

  function updateScaling({ innerWidth: viewport_width, innerHeight: viewport_height }) {
    const { offsetWidth, offsetHeight } = page_container;

    const scale = Math.min(
      viewport_width / offsetWidth,
      viewport_height / offsetHeight
    );

    var widthDiff = (offsetWidth - viewport_width) / 2.0;
    setScale(document.body, scale, widthDiff);
  }

  window.addEventListener('resize', function(event) {
    window.requestAnimationFrame(() => updateScaling(window));
  });

  document.body.classList.add('scale-to-screen');
  updateScaling(window);
}
